
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register, AuthError } from '../services/auth';
import { validateSignupForm, passwordsMatch } from '../utils/validation';
import { UsernameInput } from '../components/UsernameInput';
import { EmailInput } from '../components/EmailInput';
import { PasswordInput } from '../components/PasswordInput';
import styles from './Login.module.css';
import { TRADING_TERMS } from '../constants/terms';

const GDPR_TEXT = `Vi behandler dine personoplysninger i overensstemmelse med gældende databeskyttelseslovgivning (GDPR). Dine oplysninger bruges kun til at oprette og administrere din konto, samt til at levere de tjenester, du har anmodet om. Vi videregiver ikke dine oplysninger til tredjepart uden dit samtykke, medmindre det kræves ved lov. Du har ret til indsigt i, berigtigelse eller sletning af dine oplysninger. Kontakt os for spørgsmål om databeskyttelse.`;

export default function Signup() {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);
    const [gdprChecked, setGdprChecked] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showGdpr, setShowGdpr] = useState(false);

    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isUsernameValid, setIsUsernameValid] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [isPasswordMatching, setIsPasswordMatching] = useState(false);

    const navigate = useNavigate();

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (password) {
            const match = passwordsMatch(password, e.target.value);
            setIsPasswordMatching(match.valid);
        }
    };

    const submit = async e => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        if (!termsChecked) {
            setError('Du skal acceptere handelsbetingelserne for at oprette konto');
            return;
        }

        if (!gdprChecked) {
            setError('Du skal acceptere GDPR-vilkårene for at oprette konto');
            return;
        }

        // Frontend validation
        const validation = validateSignupForm({ email, username, password, confirmPassword });
        if (!validation.isValid) {
            setFieldErrors(validation.errors);
            return;
        }

        setLoading(true);

        try {
            await register({ email, username, password });
            // Notify other tabs/windows of auth change
            window.dispatchEvent(new Event('auth-changed'));
            navigate('/');
        } catch (err) {
            if (err instanceof AuthError) {
                // Handle field-specific errors from backend
                if (Object.keys(err.fieldErrors).length > 0) {
                    setFieldErrors(err.fieldErrors);
                } else {
                    setError(err.message);
                }
            } else {
                setError(err.message || 'Registrering mislykkedes');
            }
        } finally {
            setLoading(false);
        }
    };

    const isFormValid =
        isEmailValid &&
        isUsernameValid &&
        isPasswordValid &&
        isPasswordMatching &&
        termsChecked &&
        gdprChecked &&
        !loading;

    return (
        <div className={styles.container}>
            <form className={styles.card} onSubmit={submit}>
                <h2>Start gratis prøveperiode</h2>

                <EmailInput
                    value={email}
                    onChange={setEmail}
                    error={fieldErrors.email}
                    onValidationChange={setIsEmailValid}
                    placeholder="Email"
                    disabled={loading}
                />

                <UsernameInput
                    value={username}
                    onChange={setUsername}
                    error={fieldErrors.username}
                    onValidationChange={setIsUsernameValid}
                    placeholder="Brugernavn"
                    disabled={loading}
                />

                <PasswordInput
                    value={password}
                    onChange={setPassword}
                    error={fieldErrors.password}
                    onValidationChange={setIsPasswordValid}
                    confirmPassword={confirmPassword}
                    placeholder="Kodeord"
                    disabled={loading}
                />

                <div>
                    <div style={{ position: 'relative' }}>
                        <input
                            placeholder="Bekræft kodeord"
                            type="password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            disabled={loading}
                            required
                            className={fieldErrors.confirmPassword ? styles.inputError : ''}
                        />
                    </div>
                    {fieldErrors.confirmPassword && (
                        <div className={styles.fieldError}>{fieldErrors.confirmPassword}</div>
                    )}
                    {password && confirmPassword && isPasswordMatching && (
                        <div style={{ marginTop: '0.4rem', padding: '0.5rem', borderRadius: '6px', backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bfdbfe', fontSize: '0.85rem' }}>
                            ✓ Kodeordene stemmer overens
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1.2rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
                        <input
                            type="checkbox"
                            checked={termsChecked}
                            onChange={e => setTermsChecked(e.target.checked)}
                            disabled={loading}
                        />
                        Jeg accepterer{' '}
                        <button
                            type="button"
                            style={{ fontWeight: 700, color: '#222', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                            onClick={() => setShowTerms(true)}
                            disabled={loading}
                        >
                            handelsbetingelserne
                        </button>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
                        <input
                            type="checkbox"
                            checked={gdprChecked}
                            onChange={e => setGdprChecked(e.target.checked)}
                            disabled={loading}
                        />
                        <span>
                            Jeg accepterer brug af persondata i henhold til
                            <button
                                type="button"
                                style={{ fontWeight: 700, color: '#222', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', marginLeft: 4 }}
                                onClick={() => setShowGdpr(true)}
                                disabled={loading}
                            >
                                GDPR
                            </button>
                        </span>
                    </label>
                </div>

                {showTerms && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowTerms(false)}>
                        <div style={{ background: '#fff', borderRadius: 10, maxWidth: 420, width: '90vw', padding: '2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                            <h3 style={{ marginTop: 0 }}>Handelsbetingelser</h3>
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: '1rem', color: '#222' }}>{TRADING_TERMS}</div>
                            <button style={{ marginTop: 18 }} onClick={() => setShowTerms(false)}>Luk</button>
                        </div>
                    </div>
                )}
                {showGdpr && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowGdpr(false)}>
                        <div style={{ background: '#fff', borderRadius: 10, maxWidth: 420, width: '90vw', padding: '2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                            <h3 style={{ marginTop: 0 }}>GDPR</h3>
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: '1rem', color: '#222' }}>{GDPR_TEXT}</div>
                            <button style={{ marginTop: 18 }} onClick={() => setShowGdpr(false)}>Luk</button>
                        </div>
                    </div>
                )}

                <button disabled={!isFormValid}>
                    {loading ? 'Opretter...' : 'Start prøveperiode'}
                </button>

                <p className={styles.signup}>Allerede medlem? <Link to="/login">Log ind her</Link></p>
            </form>

            {error && <div className={styles.error}>{error}</div>}
        </div>
    );
}
